import request from "../shared/http";
import appConfig from "../shared/js/app-config";
import API_CONFIG from "../config/api.config";
import axios from "axios";
import { getAuthToken } from "../shared/authToken";

class QuotationService {
  getServiceId() {
    return appConfig.getServiceId();
  }

  basePath() {
    return `/quotations/${this.getServiceId()}`;
  }

  async getQuotations({
    search,
    status,
    leadId,
    contactId,
    createdByAccountId,
    dateFrom,
    dateTo,
    page = 1,
    limit = 25,
  } = {}) {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (status) params.set("status", status);
    if (leadId) params.set("lead_id", leadId);
    if (contactId) params.set("contact_id", contactId);
    if (createdByAccountId) params.set("created_by_account_id", createdByAccountId);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    params.set("page", String(page));
    params.set("limit", String(limit));
    const { data } = await request.get(`${this.basePath()}?${params.toString()}`);
    return data || { quotations: [], page: 1, limit: 25, total: 0, totalPages: 1 };
  }

  async getStatusSummary() {
    const { data } = await request.get(`${this.basePath()}/summary`);
    return Array.isArray(data) ? data : [];
  }

  async getNextNumber() {
    const { data } = await request.get(`${this.basePath()}/next-number`);
    return data || { quotation_number: 1, quotation_label: "QUOT-1" };
  }

  async getQuotationById(id) {
    const { data } = await request.get(`${this.basePath()}/${id}`);
    return data;
  }

  async createQuotation(payload) {
    const { data } = await request.post(this.basePath(), payload);
    return data;
  }

  async updateQuotation(id, payload) {
    const { data } = await request.put(`${this.basePath()}/${id}`, payload);
    return data;
  }

  async updateStatus(id, status) {
    const { data } = await request.patch(`${this.basePath()}/${id}/status`, { status });
    return data;
  }

  async deleteQuotation(id) {
    await request.delete(`${this.basePath()}/${id}`);
  }

  async downloadPdf(id) {
    const url = `${API_CONFIG.BASE_URL}/w1/quotations/${this.getServiceId()}/${id}/pdf`;
    const token = getAuthToken();
    const response = await axios.get(url, {
      withCredentials: true,
      responseType: "blob",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    return {
      blob: response.data,
      filename: `Quotation-${id}.pdf`,
    };
  }

  async sendQuotation(id, {
    deliveryType = "pdf",
    messageType,
    templateId,
    variables,
    urlVariable,
    messageText,
  } = {}) {
    const payload = {
      delivery_type: deliveryType || "pdf",
    };
    if (messageType) payload.message_type = messageType;
    if (templateId) payload.template_id = templateId;
    if (variables) payload.variables = variables;
    if (urlVariable) payload.url_variable = urlVariable;
    if (messageText != null) payload.message_text = messageText;

    const { data } = await request.post(`${this.basePath()}/${id}/send`, payload);
    return data;
  }

  async getSendOptions(id) {
    const { data } = await request.get(`${this.basePath()}/${id}/send-options`);
    return data;
  }

  async getConvertOptions() {
    const { data } = await request.get(`${this.basePath()}/convert-options`);
    return data;
  }

  async convertToOrder(id, { deliveryAddress, paymentMode } = {}) {
    const { data } = await request.post(`${this.basePath()}/${id}/convert-to-order`, {
      delivery_address: deliveryAddress || undefined,
      payment_mode: paymentMode || "cod",
    });
    return data;
  }

  async convertToInvoice(id) {
    const { data } = await request.post(`${this.basePath()}/${id}/convert-to-invoice`);
    return data;
  }
}

export default new QuotationService();
